/**
 * Home's KPI strip: five tiles standing on the page, no card around them. Watching now first (live,
 * refreshed every minute), then the range's four (watch time, plays, viewers, the vertical's
 * headline), each with its change against the period before and its sparkline over the same days.
 * Before anyone has watched, the range tiles show a dash (the trend chart beside them says why),
 * never a row of zeros. Placeholders keep the tiles' shape while the numbers load.
 *
 * Phones: Watching now across, then two by two. Tablets: two, then three. Wide screens: five in a row.
 */

import Skeleton from "@web-ui/components/Skeleton";
import Stat from "@web-ui/components/Stat";
import type { Realtime } from "../api";
import { MetricLabel } from "../components/metric-label";
import { MetricTile } from "../components/metric-tile";
import { definition } from "../definitions";
import { count, minuteLabel, NONE } from "../format";
import type { Kpis } from "../use-kpis";
import type { Report } from "../use-report";
import type { Pack } from "@/vocabulary/vocabulary";

const SPANS = ["col-span-2 md:col-span-3 xl:col-span-1", "md:col-span-3 xl:col-span-1", "md:col-span-2 xl:col-span-1", "md:col-span-2 xl:col-span-1", "md:col-span-2 xl:col-span-1"];
const RANGE_TILES = 4;

export function KpiStrip({ kpis, realtime, pack }: { kpis: Kpis; realtime: Report<Realtime>; pack: Pack }) {
  const unavailable = kpis.summary.failed && !kpis.summary.data;
  const stale = kpis.summary.stale;
  const range = kpis.tiles ? kpis.tiles.map((k) => (
    kpis.empty || unavailable
      ? <MetricTile key={k.id} definition={k.definition} value={NONE} />
      : <MetricTile key={k.id} definition={k.definition} value={k.value} change={k.change} upIsGood={k.id !== "ended"} trend={k.trend} trendPartial={k.trendPartial} />
  )) : Array.from({ length: RANGE_TILES }, (_, i) => <TilePlaceholder key={i} />);

  return (
    <section aria-label="Key numbers" className={`motion-stagger grid grid-cols-2 gap-3 md:grid-cols-6 xl:grid-cols-5 transition-opacity ${stale ? "opacity-60" : ""}`} aria-busy={stale || undefined}>
      <div className={`flex min-w-0 flex-col ${SPANS[0]}`}><WatchingNow report={realtime} pack={pack} /></div>
      {range.map((tile, i) => <div key={i} className={`flex min-w-0 flex-col ${SPANS[i + 1]}`}>{tile}</div>)}
    </section>
  );
}

/** Devices playing at this moment, with when it was last asked. */
function WatchingNow({ report, pack }: { report: Report<Realtime>; pack: Pack }) {
  const r = report.data;
  if (!r && !report.failed) return <TilePlaceholder />;
  const updated = r?.lastHour.at(-1)?.minute;
  return (
    <Stat className="h-full" label={<MetricLabel definition={definition("watchingNow", pack)} />} value={r ? count(r.watchingNow) : NONE}
      note={<span className="text-caption text-t-tertiary">{!r ? "Not available right now" : updated ? `Updated ${minuteLabel(updated)}` : "Updated every minute"}</span>} />
  );
}

/** A tile's shape while its number loads: label, figure, change and sparkline lines. */
function TilePlaceholder() {
  return <div className="stat h-full" aria-hidden="true"><Skeleton lines={4} /></div>;
}
